import { useQuery } from '@apollo/client';
import { Badge } from '@synoptic/ui-kit/badge/badge.js';
import { Checkbox } from '@synoptic/ui-kit/checkbox/checkbox.js';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@synoptic/ui-kit/collapsible/collapsible.js';
import { MinusIcon } from '@synoptic/ui-kit/icons/react/minus.js';
import { NavArrowDownIcon } from '@synoptic/ui-kit/icons/react/nav-arrow-down.js';
import { PlusIcon } from '@synoptic/ui-kit/icons/react/plus.js';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@synoptic/ui-kit/popover/popover.js';
import { Spinner } from '@synoptic/ui-kit/spinner/spinner.js';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@synoptic/ui-kit/tooltip/tooltip.js';
import { useCallback, useEffect, useState } from 'react';
import { gql } from '@/__generated__';
import { Stream } from '@/__generated__/schema-types';
import { StreamSourceIdsMap } from './stream-source-ids';
import {
  chevron,
  chipBadge,
  chipBadgeText,
  countBadge,
  emptySearchResult,
  headerSummary,
  noShrinkChipBadge,
  popoverFooter,
  popoverHeader,
  rowRight,
  streamName,
  streamRow,
  streamsList,
  targetItem,
  targetItemText,
  targetsList,
  targetsPopover,
  tooltipTargetChipText,
  tooltipTargets,
} from '../custom-targets-section/custom-targets-section.css';
import { expandableSwitchGroupLabel, group } from '../list-form.css';

const ListFormStreamSourcesQuery = gql(`
  query ListFormStreamSourcesQuery($id: ID!) {
    stream(id: $id) {
      id
      sources {
        sourceId
        name
      }
    }
  }
`);

const PREVIEW_SOURCES_COUNT = 2;

export const StreamSourcesSection = ({
  sources,
  value,
  onChange,
}: {
  sources: Stream[];
  value: StreamSourceIdsMap;
  onChange: (value: StreamSourceIdsMap) => void;
}) => {
  const [open, setOpen] = useState(false);
  const [sourceCountByStreamId, setSourceCountByStreamId] = useState<
    Record<string, number>
  >({});

  const rememberSourceCount = useCallback((streamId: string, count: number) => {
    setSourceCountByStreamId((prev) =>
      prev[streamId] === count ? prev : { ...prev, [streamId]: count },
    );
  }, []);

  const setStreamSourceIds = (streamId: string, sourceIds?: string[]) => {
    if (sourceIds === undefined) {
      const { [streamId]: _removed, ...rest } = value;
      onChange(rest);
    } else {
      onChange({ ...value, [streamId]: sourceIds });
    }
  };

  const qualifyingStreams = sources.filter(
    (stream) => (sourceCountByStreamId[stream.id] ?? 0) >= 2,
  );

  const summary = `${qualifyingStreams.length} ${
    qualifyingStreams.length === 1 ? 'stream' : 'streams'
  }`;

  return (
    <>
      {sources.map((stream) => (
        <StreamSourcesSync
          key={stream.id}
          streamId={stream.id}
          explicitSourceIds={value[stream.id]}
          onExplicitSourceIdsChange={(sourceIds) =>
            setStreamSourceIds(stream.id, sourceIds)
          }
          onSourceCount={rememberSourceCount}
        />
      ))}
      {qualifyingStreams.length === 0 ? null : (
        <div className={group}>
          <Collapsible
            open={open}
            onOpenChange={setOpen}
            data-testid="stream-sources"
          >
            <CollapsibleTrigger asChild>
              <div className={expandableSwitchGroupLabel}>
                Sources
                {!open && <span className={headerSummary}>{summary}</span>}
                {open ? (
                  <MinusIcon width={20} height={20} />
                ) : (
                  <PlusIcon width={20} height={20} />
                )}
              </div>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className={streamsList}>
                {qualifyingStreams.map((stream) => (
                  <StreamSourcesRow
                    key={stream.id}
                    stream={stream}
                    explicitSourceIds={value[stream.id]}
                    onExplicitSourceIdsChange={(sourceIds) =>
                      setStreamSourceIds(stream.id, sourceIds)
                    }
                  />
                ))}
              </div>
            </CollapsibleContent>
          </Collapsible>
        </div>
      )}
    </>
  );
};

const StreamSourcesSync = ({
  streamId,
  explicitSourceIds,
  onExplicitSourceIdsChange,
  onSourceCount,
}: {
  streamId: string;
  explicitSourceIds?: string[];
  onExplicitSourceIdsChange: (sourceIds: string[] | undefined) => void;
  onSourceCount: (streamId: string, count: number) => void;
}) => {
  const { data } = useQuery(ListFormStreamSourcesQuery, {
    variables: { id: streamId },
  });
  const catalog = data?.stream?.sources;

  useEffect(() => {
    if (!catalog) {
      return;
    }
    onSourceCount(streamId, catalog.length);
  }, [catalog, onSourceCount, streamId]);

  useEffect(() => {
    if (!catalog || explicitSourceIds === undefined) {
      return;
    }
    const knownSourceIds = new Set(catalog.map((source) => source.sourceId));
    if (explicitSourceIds.some((sourceId) => !knownSourceIds.has(sourceId))) {
      const pruned = explicitSourceIds.filter((sourceId) =>
        knownSourceIds.has(sourceId),
      );
      onExplicitSourceIdsChange(
        pruned.length === 0 || pruned.length === catalog.length
          ? undefined
          : pruned,
      );
    }
  }, [catalog, explicitSourceIds, onExplicitSourceIdsChange]);

  return null;
};

const StreamSourcesRow = ({
  stream,
  explicitSourceIds,
  onExplicitSourceIdsChange,
}: {
  stream: Stream;
  /** Explicit subset picked by the user; undefined = all sources */
  explicitSourceIds?: string[];
  onExplicitSourceIdsChange: (sourceIds: string[] | undefined) => void;
}) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const { data, loading } = useQuery(ListFormStreamSourcesQuery, {
    variables: { id: stream.id },
  });
  const catalog = data?.stream?.sources ?? [];

  const selectedSources =
    explicitSourceIds === undefined
      ? catalog
      : catalog.filter((source) => explicitSourceIds.includes(source.sourceId));
  const selectedSourceIds = selectedSources.map((source) => source.sourceId);
  const selectedSourceIdSet = new Set(selectedSourceIds);

  const updateSelection = (next: string[]) => {
    onExplicitSourceIdsChange(
      catalog.length > 0 && next.length === catalog.length ? undefined : next,
    );
  };

  const previewSources = selectedSources.slice(0, PREVIEW_SOURCES_COUNT);
  const hiddenSelectedSources = selectedSources.slice(PREVIEW_SOURCES_COUNT);
  const isSubset =
    selectedSources.length > 0 && selectedSources.length < catalog.length;
  const allSelected =
    catalog.length > 0 && selectedSources.length === catalog.length;

  const toggleSource = (sourceId: string) => {
    updateSelection(
      selectedSourceIdSet.has(sourceId)
        ? selectedSourceIds.filter((id) => id !== sourceId)
        : [...selectedSourceIds, sourceId],
    );
  };

  return (
    <Popover modal open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
      <PopoverTrigger asChild>
        <button type="button" className={streamRow}>
          <span className={streamName}>{stream.name}</span>
          <Badge color="neutral" className={countBadge}>
            {loading
              ? '…'
              : isSubset
                ? `${selectedSources.length}/${catalog.length}`
                : 'All'}
          </Badge>
          <span className={rowRight}>
            {loading ? null : selectedSources.length === 0 ? (
              <Badge color="neutral" className={noShrinkChipBadge}>
                All sources
              </Badge>
            ) : (
              <>
                {previewSources.map((source) => (
                  <Badge
                    key={source.sourceId}
                    color="neutral"
                    className={chipBadge}
                  >
                    <span className={chipBadgeText}>{source.name}</span>
                  </Badge>
                ))}
                {hiddenSelectedSources.length > 0 ? (
                  <Tooltip delayDuration={100}>
                    <TooltipTrigger asChild>
                      <Badge color="neutral" className={noShrinkChipBadge}>
                        +{hiddenSelectedSources.length}
                      </Badge>
                    </TooltipTrigger>
                    <TooltipContent
                      render={() => (
                        <span className={tooltipTargets}>
                          {hiddenSelectedSources.map((source) => (
                            <Badge
                              key={source.sourceId}
                              color="neutral"
                              className={chipBadge}
                            >
                              <span className={tooltipTargetChipText}>
                                {source.name}
                              </span>
                            </Badge>
                          ))}
                        </span>
                      )}
                    />
                  </Tooltip>
                ) : null}
              </>
            )}
            <NavArrowDownIcon width={16} height={16} className={chevron} />
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        className={targetsPopover}
        side="bottom"
        align="start"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className={popoverHeader}>
          <span>Sources</span>
          <Checkbox
            checked={
              allSelected
                ? true
                : selectedSources.length > 0
                  ? 'indeterminate'
                  : false
            }
            disabled={catalog.length === 0}
            aria-label={`Toggle all sources of ${stream.name}`}
            onCheckedChange={() =>
              updateSelection(
                allSelected ? [] : catalog.map((source) => source.sourceId),
              )
            }
          />
        </div>
        <div className={targetsList}>
          {loading ? (
            <div className={emptySearchResult}>
              <Spinner size={16} />
            </div>
          ) : (
            catalog.map((source) => (
              <label key={source.sourceId} className={targetItem}>
                <Checkbox
                  checked={selectedSourceIdSet.has(source.sourceId)}
                  aria-label={`Toggle source ${source.name}`}
                  onCheckedChange={() => toggleSource(source.sourceId)}
                />
                <span className={targetItemText}>{source.name}</span>
              </label>
            ))
          )}
        </div>
        <div className={popoverFooter}>
          <span>
            {selectedSources.length > 0
              ? `${selectedSources.length} of ${catalog.length} selected`
              : 'All sources included'}
          </span>
        </div>
      </PopoverContent>
    </Popover>
  );
};
